import React, {
    useContext,
    useMemo,
    useState,
    useEffect,
    useRef
} from 'react';
import { useMutation } from '@tanstack/react-query';

import { getBasketData, refetchBasketData, NotificationContext, handleError } from "../router"
import * as OPTIONS from "../option-fields";
import * as QUERIES from "../query-functions";
import * as UTILS from "../utils";

import CrossIcon from "../svg/cross";
import MinusIcon from "../svg/minus";
import HeartIcon from "../svg/heart";
import TickIcon from '../svg/tick';
import LoadingIndicator from '../components/loading-indicator';

const fundId = OPTIONS.ticketDonations.fund?.innerHTML;

export default function DonatePerTicket(props) {

    // If the donation max value is set in the CMS use that value else use double the ticket value
    const donation_max = 
            OPTIONS.ticketDonations.donation_max?.innerHTML ? Number(OPTIONS.ticketDonations.donation_max.innerHTML) 
                : Number(props.ticket?.total) * 2;

    const {notification, setNotification} = useContext(NotificationContext);
    
    const { data: basketData } = getBasketData();

    const rangeMin = useMemo(() => {
        return Number(props.ticket.total?.toFixed(2));
    }, [props.ticket.total]);
    
    const rangeMax = useMemo(() => {
        const max = donation_max + props.ticket.total;
        return Number(max.toFixed(2));
    }, [donation_max, props.ticket.total]);
    
    const formattedDate = useMemo(() => {
        return UTILS.formatDateTime(props.ticket.instance.start);
    }, [props.ticket]);

    // If the ticket has already had a donation added to it make the rangevalue equivalent to that on load
    // else make the range value a percentage of the max
    const percentage_of_max = 40;
    const [rangeValue, setRangeValue] = useState(props.ticket?.attribute_TicketDonation ? props.ticket.total + props.ticket.attribute_TicketDonation : props.ticket.total + ((percentage_of_max / 100) * donation_max));
    
    // This donationAmount is used to add the donation to the ticket so it shouldn't be timesed by the ticket amount at any point or the donation being added will be too high.
    // To display the donation timesed by the amount of tickets use (donationAmount  * props.allTicketIds.length) in ticketTotals
    const [donationAmount, setDonationAmount] = useState(rangeValue - props.ticket.total);

    // This total will be used to display the figures, but should not be used to work out the actual donation. 
    // Use props.ticket.total to work out donation amounts
    const [total, setTotal] = useState(props.applyToAllTickets ? Number(props.allTicketsTotal) : Number(props.ticket.total));

    // Use donationApplied to show correct buttons
    const [donationApplied, setDonationApplied] = useState(+donationAmount.toFixed(2) == props.ticket.attribute_TicketDonation);

    // Use ticketTotals to display the correct amounts in the card broken down by whole and decimal
    const [ticketTotals, setTicketTotals] = useState(null);

    const pathRef = useRef(null);
    const ballRef = useRef(null);
    
    const addDonation = useMutation({
        mutationFn: (body) => QUERIES.addDonation(body),
        onError: (error) => {
            handleError(error.message, setNotification);
        },
        onSuccess: () => {
            setDonationApplied(true);
            if (!props.applyToAllTickets) props.setCarouselIndex(props.carouselIndex + 1);
        },
    });
    
    const removeDonation = useMutation({
        mutationFn: (donationId) => QUERIES.removeDonation(donationId),
        onError: (error) => {
            handleError(error.message, setNotification);
        },
        onSuccess: () => {
            setDonationApplied(true);
            if (!props.applyToAllTickets) props.setCarouselIndex(props.carouselIndex + 1);
        },
    });
    
    const patchDonation = useMutation({
        mutationFn: (donationId) => QUERIES.patchDonation(donationId, [{ "Amount": donationAmount }]),
        onError: (error) => {
            handleError(error.message, setNotification);
        },
        onSuccess: () => {
            setDonationApplied(true);
            if (!props.applyToAllTickets) props.setCarouselIndex(props.carouselIndex + 1);
        },
    });
    
    const patchTicket = useMutation({
        mutationFn: (ticketId) => QUERIES.patchTicket(ticketId, [{ "Attribute_Ticket Donation": donationAmount }]),
        onError: (error) => {
            handleError(error.message, setNotification);
        },
    });
    
    const handleApplyDonation = async (e) => {
        if (!e) return;
    
        const ticket_ids = props.applyToAllTickets ? props.allTicketIds : [props.ticket.id];
        let add_donation_ids = [];

        // Create an array or queries to be ran in order which allows them all to complete before refetching the basket
        let mutationPromises = [];
    
        for (let i = 0; i < ticket_ids.length; i++) {
            const donation_has_id = UTILS.findDonationByRecognitionName(basketData?.donations, ticket_ids[i]);
    
            // If donation already exists either patch or remove it
            if (donation_has_id) {
                // Patch ticket/donation queries must be called once per ticket/donation
                // so add a query to the array for each id
                if (donationAmount == 0) {
                    mutationPromises.push(removeDonation.mutateAsync(donation_has_id.id));
                } else {
                    mutationPromises.push(patchDonation.mutateAsync(donation_has_id.id));
                }
            } else {
                // Else add or skip it
                if (donationAmount !== 0) {
                    // All donations are added in one call with an array of body object
                    // which is created below
                    add_donation_ids.push(ticket_ids[i]);
                } else {
                    if (!props.applyToAllTickets) props.setCarouselIndex(props.carouselIndex + 1); // no donation, just skip
                    setDonationApplied(true);
                }
            }
        }
    
        // Add donation mutations
        if (add_donation_ids.length) {
            const body_object = {Amount: donationAmount.toFixed(2), FundId: fundId};
            const body_arr = add_donation_ids.map(id => ({
                ...body_object,
                recognitionName: id,
            }));
            // Only add one query for new donations
            mutationPromises.push(addDonation.mutateAsync(body_arr));
        }
    
        try {
            props.setQueriesPending(true);
            await Promise.all(mutationPromises);
    
            // Patch ticket attributes after all donation actions are done
            const patchTicketPromises = props.applyToAllTickets
                ? props.allTicketIds.map(id => patchTicket.mutateAsync(id))
                : [patchTicket.mutateAsync(props.ticket.id)];
    
            await Promise.all(patchTicketPromises);
    
            // Now that all mutations are complete:
            refetchBasketData();
            props.setQueriesPending(false);
        } catch (error) {
            handleError(error.message, setNotification);
        }
    };
    
    useEffect(() => {
        setTotal(props.applyToAllTickets ? Number(props.allTicketsTotal) : Number(props.ticket.total));
    }, [props.ticket, props.applyToAllTickets]);

    useEffect(() => {
        if (props.ticket.attribute_TicketDonation == null) return;
        // If apply setting is changed then update the range value
        setRangeValue(props.ticket.total + props.ticket.attribute_TicketDonation);
    }, [props.ticket]);


    const DEBOUNCE_DELAY = 5; // to help with lag on Windows 10 Chrome
    const svgUpdateTimeout = useRef(null);

    useEffect(() => {
        if(!rangeValue || !pathRef.current || !ballRef.current) return;

        // Work out the numbers
        const new_donation_amount = +(rangeValue - props.ticket.total).toFixed(2);
        setDonationAmount(new_donation_amount);

        if (new_donation_amount == props.ticket.attribute_TicketDonation) {
            setDonationApplied(true);
        } else {
            setDonationApplied(false);
        }

        // Position the SVG
        if (svgUpdateTimeout.current) {
            clearTimeout(svgUpdateTimeout.current);
        }

        svgUpdateTimeout.current = setTimeout(() => {
            const path = pathRef.current;
            const ball = ballRef.current;
            const pathLength = path.getTotalLength();
            const progress = (rangeValue - rangeMin) / (rangeMax - rangeMin);
            const point = path.getPointAtLength(progress * pathLength);

            const bbox = ball.getBBox();
            const centerOffsetX = bbox.x + bbox.width / 2;
            const centerOffsetY = bbox.y + bbox.height / 2;

            ball.setAttribute(
                'transform',
                `translate(${point.x - centerOffsetX}, ${point.y - centerOffsetY})`
            );

            const visibleLength = progress * pathLength;
            path.style.strokeDasharray = `${visibleLength} ${pathLength}`;
            path.style.strokeDashoffset = '0';
        }, DEBOUNCE_DELAY);

    }, [rangeValue, rangeMin, rangeMax]);

    useEffect(() => {
        // Work out displayed ticket totals
        if (props.applyToAllTickets) {
            const donation_times_tickets = donationAmount * props.allTicketIds.length;
            const ticket_total =  Number(total + donation_times_tickets);

            setTicketTotals(
                {
                    total: ticket_total,
                    whole: ticket_total.toFixed(2).split('.')[0],
                    decimal: ticket_total.toFixed(2).split('.')[1]
                });
        } else {
            const ticket_total =  Number(total + donationAmount);
            
            setTicketTotals(
                {
                    total: ticket_total,
                    whole: ticket_total.toFixed(2).split('.')[0],
                    decimal: ticket_total.toFixed(2).split('.')[1]
                });
        }
    }, [total, donationAmount]);

    if (!props.ticket) return;

    return (
        <div className='ticket-border'>
            <div className='donate-per-ticket'>
                <header className='donate-per-ticket__heading'>

                    <span className='donate-per-ticket__heading--primary'>
                        <h2 className='donate-per-ticket__header'>{props.ticket.event.name}</h2>
                        {props.applyToAllTickets ?
                            <p className='donate-per-ticket__heading--info'>
                                {props.allTicketIds.length} tickets
                            </p>
                        : (props.ticket.planName || props.ticket.seat?.name) &&
                            <p className='donate-per-ticket__heading--info'>
                                {props.ticket.planName} {props.ticket.seat?.name}
                            </p>
                        }
                    </span>

                    <span className='donate-per-ticket__heading--secondary'>
                        {formattedDate &&
                            <p className='donate-per-ticket__heading--date'>{formattedDate}</p>
                        }
                    </span>
                </header>

                <p className='donate-per-ticket__summary' aria-live="polite" aria-atomic="true">
                    <span className='donate-per-ticket__summary--total'>
                        £<span>{ticketTotals?.whole}</span>.{ticketTotals?.decimal}
                    </span>
                    <span className='donate-per-ticket__summary--donation'>
                        {donationAmount == 0 ?
                            `No donation`
                        : props.applyToAllTickets ?
                            `inc. £${(donationAmount * props.allTicketIds.length).toFixed(2)} donation`
                        :
                            `inc. £${donationAmount.toFixed(2)} donation`
                        }
                    </span>
                </p>

                <div className='donate-per-ticket__slider'>

                    <label className='donate-per-ticket__slider--label' htmlFor={`donation${props.ticket.id}`}>Slide to top up</label>
                    <div className='donate-per-ticket__slider--input'>
                        <input  id={`donation${props.ticket.id}`}
                                type="range" 
                                min={rangeMin} 
                                step=".5" 
                                max={rangeMax}
                                value={rangeValue}
                                onChange={(e) => { if(e) setRangeValue(Number(e.target.value)) }}></input>

                        <svg className='donate-per-ticket__slider--arc' xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 275 149">
                            <g className="arc">
                                <g filter="url(#filter0_if_16_15)">
                                    <path fill="#F7F7F7" fillRule="evenodd" d="M20.95 2.84A21.65 21.65 0 0 1 45.08 21.7a92.72 92.72 0 0 0 184.12-.43 21.65 21.65 0 1 1 43 5.09 136.02 136.02 0 0 1-270.1.62A21.65 21.65 0 0 1 20.95 2.84Z" clipRule="evenodd"/>
                                    <path stroke="url(#paint0_linear_16_15)" strokeLinecap="round" strokeOpacity=".1" strokeWidth="2.71" d="M21.11 4.19a20.3 20.3 0 0 1 22.63 17.67 94.08 94.08 0 0 0 186.8-.44 20.3 20.3 0 1 1 40.31 4.77 134.68 134.68 0 0 1-267.4.62A20.3 20.3 0 0 1 21.1 4.19Z"/>
                                </g>
                                <path fill="#000" fillOpacity=".6" d="M188.07 115.74a.9.9 0 0 0-.56-1.15l-7.66-2.67a.9.9 0 0 0-.6 1.7l6.82 2.38-2.38 6.81a.9.9 0 1 0 1.7.6l2.68-7.67Zm-47.31 9.63v-.9.9Zm-46.85-9.11.78.37.76-1.63-.76-.37-.78 1.63Zm5.53 2.43c.53.22 1.07.43 1.6.63l.65-1.68-1.58-.62-.67 1.67Zm6.5 2.34 1.65.5.52-1.72-1.62-.5-.55 1.72Zm6.64 1.88 1.68.4.4-1.76-1.64-.4-.44 1.76Zm6.75 1.44 1.7.3.3-1.78c-.57-.1-1.13-.19-1.68-.3l-.33 1.78Zm6.81 1.04 1.72.2.19-1.8c-.57-.06-1.13-.12-1.69-.2l-.22 1.8Zm6.87.63 1.72.1.09-1.8-1.7-.1-.11 1.8Zm6.89.25h.86v-1.8h-.85l-.01 1.8Zm.86 0h.86l-.01-1.8h-.85v1.8Zm6.03-.15 1.72-.1-.12-1.8-1.7.1.1 1.8Zm6.87-.54c.57-.06 1.14-.12 1.71-.2l-.22-1.78-1.68.2.19 1.78Zm6.83-.93 1.7-.3-.32-1.77-1.68.3.3 1.77Zm6.77-1.34 1.67-.4-.43-1.76-1.65.4.4 1.76Zm6.67-1.77 1.64-.51-.54-1.72-1.62.5.52 1.73Zm6.53-2.22c.54-.2 1.08-.41 1.61-.63l-.67-1.67-1.57.62.63 1.68Zm6.37-2.7.78-.36-.79-1.63-.76.37.77 1.63Zm1.24-.88a.9.9 0 0 0-.56-1.15l-7.66-2.67a.9.9 0 0 0-.6 1.7l6.82 2.38-2.38 6.81a.9.9 0 1 0 1.7.6l2.68-7.67Zm-47.31 9.63v-.9.9Zm-46.85-9.11.78.37.76-1.63-.76-.37-.78 1.63Zm5.53 2.43c.53.22 1.07.43 1.6.63l.65-1.68-1.58-.62-.67 1.67Zm6.5 2.34 1.65.5.52-1.72-1.62-.5-.55 1.72Zm6.64 1.88 1.68.4.4-1.76-1.64-.4-.44 1.76Zm6.75 1.44 1.7.3.3-1.78c-.57-.1-1.13-.19-1.68-.3l-.33 1.78Zm6.81 1.04 1.72.2.19-1.8c-.57-.06-1.13-.12-1.69-.2l-.22 1.8Zm6.87.63 1.72.1.09-1.8-1.7-.1-.11 1.8Zm6.89.25h.86v-1.8h-.85l-.01 1.8Zm.86 0h.86l-.01-1.8h-.85v1.8Zm6.03-.15 1.72-.1-.12-1.8-1.7.1.1 1.8Zm6.87-.54c.57-.06 1.14-.12 1.71-.2l-.22-1.78-1.68.2.19 1.78Zm6.83-.93 1.7-.3-.32-1.77-1.68.3.3 1.77Zm6.77-1.34 1.67-.4-.43-1.76-1.65.4.4 1.76Zm6.67-1.77 1.64-.51-.54-1.72-1.62.5.52 1.73Zm6.53-2.22c.54-.2 1.08-.41 1.61-.63l-.67-1.67-1.57.62.63 1.68Zm6.37-2.7.78-.36-.79-1.63-.76.37.77 1.63Z"/>
                            </g>

                            <path className="gradient" ref={pathRef} stroke="url(#paint1_radial_16_15)" strokeLinecap="round" strokeWidth="39.69" d="M23.4 24.28a114.37 114.37 0 0 0 227.1.53"/>
                            
                            <g className="ball" ref={ballRef}>
                                <rect width="28.49" height="28.49" x="8.73" y="8.97" stroke="#E62645" strokeWidth="11.95" rx="14.25"/>
                                <rect width="31.25" height="31.25" x="7.36" y="7.6" fill="#F5F5F5" rx="15.63"/>
                                <rect width="31.25" height="31.25" x="7.36" y="7.6" stroke="url(#paint2_linear_16_15)" strokeWidth="1.84" rx="15.63"/>
                            </g>

                            <defs>
                                <linearGradient id="paint0_linear_16_15" x1="133.54" x2="133.54" y1="142.98" y2="0" gradientUnits="userSpaceOnUse">
                                    <stop stopColor="#F9FEFC"/>
                                    <stop offset="1" stopColor="#B9B2B3"/>
                                </linearGradient>
                                <linearGradient id="paint2_linear_16_15" x1="22.98" x2="22.98" y1="6.68" y2="39.77" gradientUnits="userSpaceOnUse">
                                    <stop stopColor="#F9FEFC"/>
                                    <stop offset="1" stopColor="#B9B2B3"/>
                                </linearGradient>
                                <radialGradient id="paint1_radial_16_15" cx="0" cy="0" r="1" gradientTransform="matrix(-231.4 0 0 -509 251.94 10.4)" gradientUnits="userSpaceOnUse">
                                    <stop offset=".05" stopColor="#E6268E"/>
                                    <stop offset=".41" stopColor="#E62645"/>
                                    <stop offset=".68" stopColor="#F74562"/>
                                    <stop offset=".97" stopColor="#E53751"/>
                                </radialGradient>
                                <filter id="filter0_if_16_15" width="274.02" height="153.28" x=".13" y=".35" colorInterpolationFilters="sRGB" filterUnits="userSpaceOnUse">
                                    <feFlood floodOpacity="0" result="BackgroundImageFix"/>
                                    <feBlend in="SourceGraphic" in2="BackgroundImageFix" result="shape"/>
                                    <feColorMatrix in="SourceAlpha" result="hardAlpha" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"/>
                                    <feOffset dy="7.22"/>
                                    <feGaussianBlur stdDeviation="9.02"/>
                                    <feComposite in2="hardAlpha" k2="-1" k3="1" operator="arithmetic"/>
                                    <feColorMatrix values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0"/>
                                    <feBlend in2="shape" result="effect1_innerShadow_16_15"/>
                                    <feGaussianBlur result="effect2_foregroundBlur_16_15" stdDeviation=".9"/>
                                </filter>
                            </defs>
                        </svg>

                    </div>
                    
                    <button type="button" 
                            className='btn btn--square donate-per-ticket__slider--increment' 
                            onClick={(e) => { if(e) setRangeValue((rangeValue + 1) > rangeMax ? rangeMax : rangeValue + 1) }}
                            disabled={rangeValue >= rangeMax}>
                        <span className='sr-text'>Increase donation</span>
                        <CrossIcon />
                    </button>
                    
                    <button type="button" 
                            className='btn btn--square donate-per-ticket__slider--decrement' 
                            onClick={(e) => { if(e) setRangeValue((rangeValue - 1) < rangeMin ? rangeMin : rangeValue - 1) }}
                            disabled={rangeValue <= rangeMin}>
                        <span className='sr-text'>Reduce donation</span>
                        <MinusIcon />
                    </button>
                </div>


                <div className='donate-per-ticket__footer'>
                
                    {donationApplied ?
                        donationAmount == 0 ?
                            <p className='btn btn--success'>I'm not donating today</p>
                        :
                        (donationAmount + total) == rangeMax ?
                            <p className='btn btn--success'>Full top-up applied <HeartIcon /></p>
                        :
                            <p className='btn btn--success'>Top-up applied <TickIcon /></p>
                    :
                        <button type='button' className='btn btn--primary' disabled={props.queriesPending} onClick={handleApplyDonation}>
                            {donationAmount == 0 ?
                                "I'm not donating today"
                            :(donationAmount + total) == rangeMax ?
                                ( <>Apply full top up <HeartIcon /></> )
                            :
                                "Apply this top-up"
                            }
                            {props.queriesPending &&
                                <LoadingIndicator />
                            }
                        </button>
                    }

                    {props.allTicketIds.length > 1 &&
                        <div className="form-field--checkbox">
                            <input type="checkbox" 
                                    name="applyAll" 
                                    id="applyAll"
                                    checked={props.applyToAllTickets}
                                    onChange={(e) => props.setApplyToAllTickets(e.target.checked)}/>
                            <label htmlFor="applyAll">Apply the same financial top-up to all of my tickets</label>
                        </div>
                    }
                </div>
            </div>
        </div>
    );
}
